import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  BarChart3, Loader2, DollarSign, Users, Building2, CalendarCheck,
  TrendingUp, TrendingDown, CheckCircle2, Clock, XCircle,
} from "lucide-react";
import { adminApi, type AnalyticsResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";

export const Route = createFileRoute("/admin/analytics")({
  component: AnalyticsPage,
});

const STATUS_COLORS: Record<string, string> = {
  confirmed: "#22c55e",
  pending: "#f59e0b",
  cancelled: "#ef4444",
  refunded: "#8b5cf6",
};

function KpiCard({ icon: Icon, label, value, sub, color }: {
  icon: React.ElementType; label: string; value: string; sub?: string; color?: string;
}) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
      <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary">
        <Icon className="size-5" style={{ color }} />
      </div>
      <div className="mt-4 font-[family-name:Inter] text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {sub && <div className="mt-1 text-[11px] text-muted-foreground/70">{sub}</div>}
    </div>
  );
}

function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    adminApi.analytics()
      .then(res => setData(res))
      .catch(err => setError(err instanceof Error ? err.message : "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-20">
          <Loader2 className="size-4 animate-spin" /> Loading analytics…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="text-sm text-destructive py-20">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const hasActivity = data.totalBookings > 0 || data.totalUsers > 0 || data.totalRevenue > 0;

  const statusData = data.bookingStatusBreakdown.map(s => ({
    name: s.status.charAt(0).toUpperCase() + s.status.slice(1),
    value: s.count,
    color: STATUS_COLORS[s.status] || "#94a3b8",
  }));

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Analytics</h1>
        <p className="text-sm text-muted-foreground">Operational performance across the platform.</p>
      </div>

      {!hasActivity ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/60 bg-card py-24 text-center shadow-soft">
          <BarChart3 className="mb-4 size-14 text-muted-foreground/30" />
          <p className="text-lg font-semibold">No data yet</p>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            Analytics will appear once your platform has bookings, users, and venue activity.
          </p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
            <KpiCard icon={DollarSign} label="Total Revenue" value={formatNaira(data.totalRevenue)} color="#22c55e" />
            <KpiCard icon={CalendarCheck} label="Total Bookings" value={String(data.totalBookings)} sub={`${data.confirmedBookings} confirmed`} />
            <KpiCard icon={Users} label="Total Users" value={String(data.totalUsers)} />
            <KpiCard icon={Building2} label="Active Venues" value={String(data.activeVenues)} sub={`${data.totalVenues} total`} />
          </div>

          {/* Charts Row 1 */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Monthly Revenue */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <h2 className="font-display text-base font-semibold mb-4">Monthly Revenue</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `₦${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => formatNaira(v)} />
                    <Bar dataKey="revenue" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Monthly Bookings */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <h2 className="font-display text-base font-semibold mb-4">Monthly Bookings</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.monthlyBookings}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* User Growth */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <h2 className="font-display text-base font-semibold mb-4">User Growth</h2>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthlyUserGrowth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Booking Status Breakdown */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <h2 className="font-display text-base font-semibold mb-4">Booking Status</h2>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="80%" label>
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={24} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft space-y-4">
              <h2 className="font-display text-base font-semibold">Overview</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-xl bg-green-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircle2 className="size-4" /> Confirmed
                  </div>
                  <span className="font-bold text-green-700 font-[family-name:Inter]">{data.confirmedBookings}</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-amber-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-amber-700">
                    <Clock className="size-4" /> Pending
                  </div>
                  <span className="font-bold text-amber-700 font-[family-name:Inter]">{data.pendingBookings}</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-red-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-red-700">
                    <XCircle className="size-4" /> Cancelled
                  </div>
                  <span className="font-bold text-red-700 font-[family-name:Inter]">{data.cancelledBookings}</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-blue-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <TrendingUp className="size-4" /> Avg Revenue / Booking
                  </div>
                  <span className="font-bold text-blue-700 font-[family-name:Inter]">
                    {formatNaira(data.totalBookings > 0 ? data.totalRevenue / data.totalBookings : 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Top Venues Table */}
          <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
            <h2 className="font-display text-base font-semibold mb-4">Top Performing Venues</h2>
            {data.topVenues.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No venue data available yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium sm:px-5">Venue</th>
                      <th className="px-4 py-3 font-medium sm:px-5">Bookings</th>
                      <th className="px-4 py-3 font-medium sm:px-5">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {data.topVenues.map((v, i) => (
                      <tr key={v.venueId} className="hover:bg-accent/30">
                        <td className="px-4 py-3 sm:px-5">
                          <span className="inline-flex items-center gap-2">
                            <span className="grid size-6 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary font-[family-name:Inter]">{i + 1}</span>
                            <span className="font-medium">{v.venueName}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 font-semibold font-[family-name:Inter] sm:px-5">{v.bookings}</td>
                        <td className="px-4 py-3 font-semibold font-[family-name:Inter] sm:px-5">{formatNaira(v.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
